import {Assert, GetValues, GetValues_ForSchema, ModifyString} from "js-vextensions";
import {O, RunInAction} from "web-vcore";
import {computed, observable} from "mobx";
import {OnSessionEnded} from "../../../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {store} from "../../index.js";
import moment from "moment";
import {AddSchema} from "mobx-graphlink";
import {ignore} from "mobx-sync";

export enum JourneyTab {
	history = "history",
	stats = "stats",
	config = "config",
	log = "log",
	engine = "engine",
}

export enum JourneySettingsTab {
	view = "view",
	sessionControl = "sessionControl",
	sessionInfo = "sessionInfo",
}

export enum BottomPanelSubpanel {
	none = "none",
	entities = "entities",
	transcribe = "transcribe",
	llm = "llm",
	media = "media",
}

export type EntityCategory = "people" | "objects" | "concepts";

export enum OverlayPanel {
	none = "none",
	normal = "normal",
	canvas = "canvas",
	doorway = "doorway",
	recallGrid = "recallGrid",
}

export class FlashConfig {
	static ToValsArray(config: FlashConfig) {
		return [config.intensity, config.flashInterval, config.switchInterval, config.x, config.y, config.z];
	}
	static FromValsArray(vals: number[]) {
		return new FlashConfig({
			intensity: vals[0],
			flashInterval: vals[1],
			switchInterval: vals[2],
			x: vals[3],
			y: vals[4],
			z: vals[5],
		});
	}

	constructor(data?: Partial<FlashConfig>) { Object.assign(this, data); }
	intensity = 100;
	flashInterval = 10;
	switchInterval = 500;
	x = 0;
	y = 1;
	z = 3;
}

export class JourneyState {
	//@O tab = JourneyTab.engine;
	@O accessor settingsTab = JourneySettingsTab.view;

	// general
	@O @ignore accessor overlay = OverlayPanel.none;
	@O @ignore accessor expandedSegments = observable.set<string>();
	ToggleSegmentExpanded(segmentKey: string) {
		RunInAction("JourneyState.ToggleSegmentExpanded", ()=>{
			if (this.expandedSegments.has(segmentKey)) this.expandedSegments.delete(segmentKey);
			else this.expandedSegments.add(segmentKey);
		});
	}

	// dropdown -> view panel
	@O accessor pastJournalEntriesToShow = 0;
	@O @ignore accessor showAnchorEntities = false;
	@O accessor showEventBoxes = true;
	@O accessor showDreamEntities = true;
	@O accessor allowScreenlessEntityAdding = true;
	@O accessor showBottomPanel = true;
	@O accessor overlay_default = OverlayPanel.normal;
	@O accessor overlay_snoozeDuration = 0; // in seconds; 0 = no snooze
	@O accessor flashConfig = new FlashConfig();

	// dropdown -> session-control panel
	@O accessor triggersDialog_hideDisabled = false;
	@O accessor triggersDialog_hideUnassigned = false;
	@O accessor triggersDialog_hideCompInactive = true;

	// bottom panel
	@O accessor bottomPanelSubpanel = BottomPanelSubpanel.none;
	@O accessor entities = new EntitiesSubpanelState();
	@O accessor transcribe = new TranscribeSubpanelState();
	@O accessor llm = new LLMSubpanelState();
}

export const GraphRenderType_values = ["bars", "spline", "linear", "stepped"] as const;
export type GraphRenderType = typeof GraphRenderType_values[number];
AddSchema("GraphRenderType", {enum: GraphRenderType_values as any});

export enum SmoothingType {
	"previous" = "previous",
	"centered" = "centered",
}
AddSchema("SmoothingType", {enum: GetValues(SmoothingType)});

export class EntitiesSubpanelState {
	// ui-layout fields
	@O @ignore accessor entityCellContainerWidth = 0;
	@O @ignore accessor entityCellColumns = 9;
	@O @ignore accessor searchText = "";
}
export class TranscribeSubpanelState {
	@O accessor vibrateOnTranscribeChunk = true;
	@O accessor transcribeText = "";

	@O @ignore accessor forceShowTranscribeText = false;
}
export class LLMSubpanelState {
	@O accessor panelHeight = 40;
	@O accessor showLLMOutputOnSide = false;
	@O accessor llmPresets = [] as LLMPreset[];

	@O accessor customLLMInput = "";
	@O accessor customLLMInput_includeSystemPrompt = true;
	@O accessor customLLMInput_includeAddendum = true;
}

export class LLMPreset {
	name: string;
	text: string;
}
export function GetDefaultLLMPresets() {
	const presets = [
		{name: "ShowNext", text: "LLM, show me what happens next."},
		{name: "SpeakMyself", text: "LLM, remember to never automatically speak or act for me. If they ask me a question, stop the generation there and allow me to respond."},
		{name: "Shrug", text: `"🤷‍♂️"`},
		{name: "FillDyn", text: "LLM, you forgot to populate the actual content for the dynamic options! Of course, also list all of the non-dynamic options like normal."},
	];
	return presets;
}

export const entityCellWidthTarget = 40;
export const entityCellAspectRatio = "1/1";

//export const entityCellWidthTarget = 60;
//export const entityCellAspectRatio = "3/2";